// Service to generate guitar chord voicings from chord names
// Adapted from ChordLib for Chord Notator integration

export interface ChordVoicing {
  id: string;
  name: string;
  frets: number[]; // Array of 6 integers. -1 = mute, 0 = open, >0 = fret number
  fingers: number[]; // Array of 6 integers. 0 = no finger, 1=index, 2=middle, 3=ring, 4=pinky
  baseFret: number;
  barres?: { fret: number; fromString: number; toString: number }[];
  difficulty?: 'Easy' | 'Medium' | 'Hard' | 'Jazz';
}

interface ChordShape {
  name: string;
  baseRoot: string;
  frets: number[];
  fingers: number[];
  rootString: number;
  barre?: boolean;
}

const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Movable chord shapes by quality
const SHAPES: Record<string, ChordShape[]> = {
  'major': [
    { name: "E Shape", baseRoot: 'E', frets: [0, 2, 2, 1, 0, 0], fingers: [0, 3, 4, 2, 0, 0], rootString: 0, barre: true },
    { name: "A Shape", baseRoot: 'A', frets: [-1, 0, 2, 2, 2, 0], fingers: [0, 0, 2, 3, 4, 0], rootString: 1, barre: true },
    { name: "D Shape", baseRoot: 'D', frets: [-1, -1, 0, 2, 3, 2], fingers: [0, 0, 0, 1, 3, 2], rootString: 2 },
    { name: "C Shape", baseRoot: 'C', frets: [-1, 3, 2, 0, 1, 0], fingers: [0, 3, 2, 0, 1, 0], rootString: 1 },
  ],
  'minor': [
    { name: "Em Shape", baseRoot: 'E', frets: [0, 2, 2, 0, 0, 0], fingers: [0, 3, 4, 0, 0, 0], rootString: 0, barre: true },
    { name: "Am Shape", baseRoot: 'A', frets: [-1, 0, 2, 2, 1, 0], fingers: [0, 0, 3, 4, 2, 0], rootString: 1, barre: true },
    { name: "Dm Shape", baseRoot: 'D', frets: [-1, -1, 0, 2, 3, 1], fingers: [0, 0, 0, 2, 3, 1], rootString: 2 },
  ],
  '7': [
    { name: "E7 Shape", baseRoot: 'E', frets: [0, 2, 0, 1, 0, 0], fingers: [0, 2, 0, 1, 0, 0], rootString: 0, barre: true },
    { name: "A7 Shape", baseRoot: 'A', frets: [-1, 0, 2, 0, 2, 0], fingers: [0, 0, 2, 0, 3, 0], rootString: 1, barre: true },
    { name: "C7 Shape", baseRoot: 'C', frets: [-1, 3, 2, 3, 1, 0], fingers: [0, 3, 2, 4, 1, 0], rootString: 1 },
    { name: "D7 Shape", baseRoot: 'D', frets: [-1, -1, 0, 2, 1, 2], fingers: [0, 0, 0, 2, 1, 3], rootString: 2 },
  ],
  'maj7': [
    { name: "Emaj7 Shape", baseRoot: 'E', frets: [0, 2, 1, 1, 0, 0], fingers: [0, 3, 1, 2, 0, 0], rootString: 0, barre: true },
    { name: "Amaj7 Shape", baseRoot: 'A', frets: [-1, 0, 2, 1, 2, 0], fingers: [0, 0, 2, 1, 3, 0], rootString: 1, barre: true },
    { name: "Cmaj7 Shape", baseRoot: 'C', frets: [-1, 3, 2, 0, 0, 0], fingers: [0, 3, 2, 0, 0, 0], rootString: 1 },
  ],
  'm7': [
    { name: "Em7 Shape", baseRoot: 'E', frets: [0, 2, 0, 0, 0, 0], fingers: [0, 2, 0, 0, 0, 0], rootString: 0, barre: true },
    { name: "Am7 Shape", baseRoot: 'A', frets: [-1, 0, 2, 0, 1, 0], fingers: [0, 0, 2, 0, 1, 0], rootString: 1, barre: true },
    { name: "Dm7 Shape", baseRoot: 'D', frets: [-1, -1, 0, 2, 1, 1], fingers: [0, 0, 0, 2, 1, 1], rootString: 2, barre: true },
  ],
  'm6': [
    { name: "Em6 Shape", baseRoot: 'E', frets: [0, 2, 2, 0, 2, 0], fingers: [0, 2, 3, 0, 4, 0], rootString: 0, barre: true },
    { name: "Am6 Shape", baseRoot: 'A', frets: [-1, 0, 2, 2, 1, 2], fingers: [0, 0, 2, 3, 1, 4], rootString: 1, barre: true },
    { name: "Dm6 Shape", baseRoot: 'D', frets: [-1, -1, 0, 2, 0, 1], fingers: [0, 0, 0, 2, 0, 1], rootString: 2 },
  ],
  'sus4': [
    { name: "Esus4", baseRoot: 'E', frets: [0, 2, 2, 2, 0, 0], fingers: [0, 2, 3, 4, 0, 0], rootString: 0, barre: true },
    { name: "Asus4", baseRoot: 'A', frets: [-1, 0, 2, 2, 3, 0], fingers: [0, 0, 2, 3, 4, 0], rootString: 1, barre: true },
  ],
  'sus2': [
    { name: "Asus2", baseRoot: 'A', frets: [-1, 0, 2, 2, 0, 0], fingers: [0, 0, 2, 3, 0, 0], rootString: 1, barre: true },
    { name: "Dsus2", baseRoot: 'D', frets: [-1, -1, 0, 2, 3, 0], fingers: [0, 0, 0, 1, 2, 0], rootString: 2 },
  ],
  'dim': [
    { name: "Dim7 (6)", baseRoot: 'G', frets: [3, -1, 2, 3, 2, -1], fingers: [2, 0, 1, 3, 1, 0], rootString: 0 },
    { name: "Dim7 (5)", baseRoot: 'C', frets: [-1, 3, 4, 2, 4, -1], fingers: [0, 2, 4, 1, 3, 0], rootString: 1 },
  ],
  'aug': [
    { name: "Aug (6)", baseRoot: 'E', frets: [0, -1, 2, 1, 1, 0], fingers: [0, 0, 3, 2, 4, 0], rootString: 0, barre: true },
    { name: "Aug (5)", baseRoot: 'C', frets: [-1, 3, 2, 1, 1, -1], fingers: [0, 3, 2, 1, 1, 0], rootString: 1 },
  ],
  '9': [
    { name: "E9", baseRoot: 'E', frets: [0, 2, 0, 1, 0, 2], fingers: [1, 2, 0, 1, 0, 3], rootString: 0, barre: true },
    { name: "C9", baseRoot: 'C', frets: [-1, 3, 2, 3, 3, -1], fingers: [0, 2, 1, 3, 4, 0], rootString: 1 },
  ],
  'm9': [
    { name: "Am9", baseRoot: 'A', frets: [-1, 0, 5, 5, 0, 0], fingers: [0, 0, 2, 3, 0, 0], rootString: 1, barre: true },
  ],
  '5': [
    { name: "5 (6)", baseRoot: 'F', frets: [1, 3, 3, -1, -1, -1], fingers: [1, 3, 4, 0, 0, 0], rootString: 0, barre: true },
    { name: "5 (5)", baseRoot: 'B', frets: [-1, 2, 4, 4, -1, -1], fingers: [0, 1, 3, 4, 0, 0], rootString: 1, barre: true },
  ],
  'mb5': [
    { name: "mb5 (6)", baseRoot: 'G', frets: [3, -1, 3, 3, 2, -1], fingers: [2, 0, 3, 4, 1, 0], rootString: 0 },
    { name: "mb5 (5)", baseRoot: 'C', frets: [-1, 3, 4, 3, 4, -1], fingers: [0, 1, 3, 2, 4, 0], rootString: 1 },
    { name: "mb5 (4)", baseRoot: 'D', frets: [-1, -1, 0, 1, 1, 1], fingers: [0, 0, 0, 2, 3, 4], rootString: 2 },
  ],
  '7b5': [
    { name: "7b5 (6)", baseRoot: 'G', frets: [3, -1, 3, 4, 2, -1], fingers: [2, 0, 3, 4, 1, 0], rootString: 0 },
    { name: "7b5 (5)", baseRoot: 'C', frets: [-1, 3, 4, 3, 5, -1], fingers: [0, 1, 2, 1, 3, 0], rootString: 1, barre: true },
  ]
};

/**
 * Parse chord name to extract root and quality
 * Examples: "Cmaj7" → {root: "C", quality: "maj7"}, "Am" → {root: "A", quality: "minor"}
 */
function parseChordName(chordName: string): { root: string; quality: string } {
  // Handle flats/sharps
  const sanitized = chordName.replace('♯', '#').replace('♭', 'b');
  
  // Extract root (C, C#, Db, etc.)
  let root = sanitized[0];
  let qualityStr = sanitized.slice(1);
  
  if (qualityStr.startsWith('#') || qualityStr.startsWith('b')) {
    root += qualityStr[0];
    qualityStr = qualityStr.slice(1);
  }
  
  // Normalize root (Db → C#)
  if (root.includes('b')) {
    const baseNote = root[0];
    const idx = NOTES.indexOf(baseNote);
    if (idx > 0) {
      root = NOTES[(idx - 1 + 12) % 12];
    }
  }
  
  // Map quality string to internal format
  const qualityMap: Record<string, string> = {
    '': 'major',
    'maj': 'major',
    'M': 'major',
    'm': 'minor',
    'min': 'minor',
    '-': 'minor',
    '7': '7',
    'maj7': 'maj7',
    'M7': 'maj7',
    'Δ7': 'maj7',
    'm7': 'm7',
    'min7': 'm7',
    '-7': 'm7',
    'm6': 'm6',
    'min6': 'm6',
    '-6': 'm6',
    'sus4': 'sus4',
    'sus2': 'sus2',
    'sus': 'sus4',
    'dim': 'dim',
    '°': 'dim',
    'aug': 'aug',
    '+': 'aug',
    '9': '9',
    'm9': 'm9',
    '5': '5',
    'mb5': 'mb5',
    'm♭5': 'mb5',
    '7b5': '7b5',
    '7♭5': '7b5',
  };
  
  const quality = qualityMap[qualityStr] || 'major';
  
  return { root, quality };
}

/**
 * Transpose a shape to target root
 */
function transposeShape(shape: ChordShape, targetRoot: string, quality: string): ChordVoicing | null {
  const targetIndex = NOTES.indexOf(targetRoot);
  const shapeRootIndex = NOTES.indexOf(shape.baseRoot);
  
  if (shapeRootIndex === -1 || targetIndex === -1) return null;

  let shift = targetIndex - shapeRootIndex;
  if (shift < 0) shift += 12;

  let baseFret = shift + 1;
  if (shift === 0) baseFret = 1;

  const newFrets = shape.frets.map(f => {
    if (f === -1) return -1;
    if (shape.barre && f === 0) return shift;
    if (f === 0) return shift;
    return f + shift;
  });

  let barres: { fret: number; fromString: number; toString: number }[] | undefined = undefined;
  if (shift > 0 && shape.barre) {
    barres = [{
      fret: shift,
      fromString: shape.rootString,
      toString: 5
    }];
  }

  const id = `${targetRoot}-${shape.name.replace(/\s/g, '')}-${baseFret}`;
  const activeFrets = newFrets.filter(f => f >= 0);
  const minFret = activeFrets.length > 0 ? Math.min(...activeFrets) : 1;
  const visualBaseFret = minFret === 0 ? 1 : minFret;

  return {
    id,
    name: `${targetRoot}${quality === 'major' ? '' : quality}`,
    frets: newFrets,
    fingers: shape.fingers,
    baseFret: visualBaseFret,
    barres,
    difficulty: shift === 0 ? 'Easy' : shape.barre ? 'Hard' : 'Medium'
  };
}

/**
 * Generate guitar voicings for a given chord
 * @param chordName - Full chord name (e.g., "Cmaj7", "Am", "G7")
 * @returns Array of ChordVoicing objects
 */
export function getGuitarVoicings(chordName: string): ChordVoicing[] {
  const { root, quality } = parseChordName(chordName);
  const voicings: ChordVoicing[] = [];
  
  const shapes = SHAPES[quality] || SHAPES['major'];
  
  shapes.forEach(shape => {
    const voicing = transposeShape(shape, root, quality);
    if (voicing) {
      const isDuplicate = voicings.some(v => JSON.stringify(v.frets) === JSON.stringify(voicing.frets));
      if (!isDuplicate) {
        voicings.push(voicing);
      }
    }
  });
  
  // Sort by position on neck
  voicings.sort((a, b) => a.baseFret - b.baseFret);
  
  return voicings;
}
